import { useUpdateOrganizationMembership } from 'api/OrganizationAPI';
import { Role } from 'api/RolesAPI';
import { useOrganizationMembersQueryContext } from 'modules/organization/contexts/OrganizationMembersQueryContext';
import {
  MODAL_NAMES,
  MemberRolePayload,
  useOrganizationModalsContext,
} from 'modules/organization/contexts/OrganizationModalsContext';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { BUTTON_VARIANTS, Button } from 'ui/button';
import { FormField } from 'ui/formField';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { BasicSelect } from 'ui/selects';
import { Body2 } from 'ui/theme';
import { createRoleOptions, updateMembershipRole } from './utils';

export const MembershipModal = () => {
  const dispatch = useDispatch();
  const { refetchAll, roles } = useOrganizationMembersQueryContext();

  const { hideModal, modalState } = useOrganizationModalsContext();
  const { member, project, role } = modalState?.payload as MemberRolePayload;

  // tracks changes to role through BasicSelect to use when submitting
  const [selectedRole, setRole] = useState(role?.name);

  // mutation to update the role
  const { mutate } = useUpdateOrganizationMembership(member?.userId, {
    onSuccess: () => {
      dispatch(
        addPositiveToast(
          <span>
            Role was updated to <b>{selectedRole}</b> successfully
          </span>,
        ),
      );
      refetchAll();
      hideModal();
    },
    onError: (error: unknown) => {
      const msg = (
        <span>
          There was an error updating role to <b>{selectedRole}</b>. Please try again.
        </span>
      );
      dispatch(addNegativeToastWithErrorId(msg, 'updateMembershipRole', error));
      hideModal();
    },
  });

  // Bail out if nothing to show
  if (!project || !member) return null;

  // UI display vars
  const isModalOpen = modalState?.name === MODAL_NAMES.MEMBERSHIP_ROLE;
  const roleOptions = createRoleOptions(roles);

  const onSubmit = () => {
    // handle submit if there is a project to associate with a role
    if (project.id) {
      const projectRole = roles.find((r: Role) => r.name === selectedRole && r.scope === 'Project');
      const teamRole = roles.find((r: Role) => r.name === selectedRole && r.scope === 'Team');

      const projectMemberships = updateMembershipRole(
        member?.projectMemberships || [],
        project.id,
        projectRole?.id,
      );

      const teamMemberships = updateMembershipRole(
        member?.teamMemberships || [],
        project.id,
        teamRole?.id,
      );

      mutate({
        projectMemberships,
        teamMemberships,
      });
    }
  };

  return (
    <>
      {isModalOpen && (
        <Modal id="membership-modal" isModalOpen={isModalOpen} onClose={hideModal}>
          <ModalHeader>Manage role</ModalHeader>
          <ModalBody>
            <Body2>Assign role for the {project.name} project</Body2>
            <FormField labelText="Role">
              <BasicSelect
                defaultValues={[selectedRole]}
                isFullWidth
                options={roleOptions}
                placeholder={'Select a role'}
                onSelect={([val]) => setRole(val)}
              />
            </FormField>
          </ModalBody>
          <ModalFooter>
            <Button
              data-test="save-membership-changes"
              variant={BUTTON_VARIANTS.PRIMARY}
              onClick={onSubmit}
            >
              Save changes
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
